import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Fade,
  Collapse,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  Storage as StorageIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudDownload as DownloadIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Upload as UploadIcon,
  Shield as ShieldIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ChevronRight as ChevronRightIcon,
  Speed as SpeedIcon,
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  Telegram as TelegramIcon,
  SmartToy as BotIcon,
  PhotoCamera as PhotoIcon,
  QrCode as QrCodeIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI, logoAPI, toursAPI, apiCall } from '../services/api';
import AdminLayout from '../components/layout/AdminLayout';

// Enhanced Settings Card Component
const SettingsCard = ({ 
  icon, 
  title, 
  description, 
  children, 
  expanded = false, 
  onChange,
  badge = null,
  status = null 
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(expanded);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (onChange) onChange(newState);
  };

  return (
    <Card 
      sx={{ 
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.main,
        },
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Box
        onClick={handleToggle}
        sx={{
          p: 3,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Badge badgeContent={badge} color="primary" variant={badge ? 'dot' : 'standard'}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: `${theme.palette.primary.main}15`,
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          </Badge>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              {status && (
                <Chip 
                  label={status.label} 
                  color={status.color} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          sx={{ 
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      
      <Collapse in={isExpanded} timeout="auto">
        <Divider />
        <Box p={3}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
};

// Quick Action Button Component
const QuickActionButton = ({ icon, label, description, onClick, color = "primary" }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6],
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Box
          sx={{
            mb: 2,
            mx: 'auto',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: `${theme.palette[color].main}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette[color].main,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// System Status Indicator
const SystemStatus = ({ label, status, description }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckIcon />;
      case 'warning': return <WarningIcon />;
      case 'error': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
      <Box>
        <Typography variant="body1" fontWeight={500}>{label}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Chip 
        icon={getStatusIcon(status)}
        label={status}
        color={getStatusColor(status)}
        size="small"
        variant="outlined"
        sx={{ textTransform: 'capitalize' }}
      />
    </Box>
  );
};

const Settings = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  
  // Telegram Bot Settings State
  const [telegramSettings, setTelegramSettings] = useState({
    botToken: '',
    webhookUrl: '',
    botUsername: '',
    botEnabled: false,
    documentUploadEnabled: true,
    staffApprovalRequired: true,
    allowedFileTypes: 'jpg,jpeg,png,pdf',
    maxFileSize: '10',
    autoProcessDocuments: true,
    botWelcomeMessage: 'Welcome! Please upload your travel documents.',
    botHelpMessage: 'Send me your documents and I\'ll help you organize them.',
  });

  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    companyName: 'Maldives Hajj Corporation Limited (MHCL)',
    companyLogo: '',
    defaultLanguage: 'en',
    timezone: 'UTC',
    dateFormat: 'DD/MM/YYYY',
    currency: 'USD',
    maxFileSize: '10',
    allowedFileTypes: 'pdf,jpg,jpeg,png',
    qrCodeBaseUrl: window.location.origin,
    emailNotifications: true,
    systemBackups: true,
    auditLogging: true,
  });

  // Logo Management State
  const [logoSettings, setLogoSettings] = useState({
    companyLogo: null,
    companyLogoFile: null,
    tourLogos: {},
    uploadingLogo: false,
    logoError: '',
    logoSuccess: '',
  });

  const [tours, setTours] = useState([]);

  // QR Design Settings State
  const [qrDesignSettings, setQRDesignSettings] = useState({
    systemDefault: null,
    templates: [],
    selectedTemplate: null,
    customDesign: {
      foregroundColor: '#000000',
      backgroundColor: '#FFFFFF',
      dotPattern: 'square',
      logoEnabled: false,
      logoSizePercent: 20
    },
    previewDataUrl: null,
    loadingTemplates: false,
    savingDesign: false,
    generatingPreview: false
  });

  // Security Settings State
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: '30',
    maxLoginAttempts: '5',
    passwordMinLength: '8',
    requireSpecialChars: true,
    twoFactorAuth: false,
    ipWhitelist: '',
    apiRateLimit: '100',
  });

  // User Profile State
  const [profileSettings, setProfileSettings] = useState({
    fullName: currentUser?.full_name || '',
    username: currentUser?.username || '',
    email: currentUser?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Admin Users State
  const [adminUsers, setAdminUsers] = useState([]);
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    fullName: '',
    email: '',
    password: '',
    role: 'Admin',
  });

  // System Status
  const [systemStatus, setSystemStatus] = useState({
    database: 'healthy',
    storage: 'healthy',
    api: 'healthy',
    backup: 'warning',
  });

  // Load initial data only when user is authenticated
  useEffect(() => {
    document.title = 'Settings - PIMS';
    if (currentUser && currentUser.id) {
      loadInitialData();
    }
  }, [currentUser]);

  const loadInitialData = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔧 Loading settings data...');
      
      // Load settings and users sequentially with better error handling
      const settingsData = await apiCall(settingsAPI.getSettings).catch((err) => {
        console.warn('Settings API failed:', err.message);
        return { settings: {} };
      });

      const usersData = await apiCall(settingsAPI.getUsers).catch((err) => {
        console.error('Users API failed:', err.message);
        setError(`Failed to load admin users: ${err.message}`);
        return { users: [] };
      });

      const systemInfoData = await apiCall(settingsAPI.getSystemInfo).catch((err) => {
        console.warn('System info API failed:', err.message);
        return { info: {} };
      });

      if (settingsData.settings) {
        setSystemSettings(prev => ({ ...prev, ...settingsData.settings }));
        setSecuritySettings(prev => ({ ...prev, ...settingsData.settings }));
        setTelegramSettings(prev => ({ ...prev, ...settingsData.settings }));
      }

      if (usersData.users) {
        setAdminUsers(usersData.users);
        console.log(`✅ Loaded ${usersData.users.length} admin users`);
      }

      // Set current user profile data
      setProfileSettings(prev => ({
        ...prev,
        fullName: currentUser?.full_name || '',
        username: currentUser?.username || '',
        email: currentUser?.email || ''
      }));

      // Load logo and QR design data
      await Promise.all([
        loadCompanyLogo(),
        loadQRDesigns(),
        loadTours()
      ]);
      
      console.log('✅ Settings data loaded successfully');
    } catch (err) {
      console.error('Failed to load settings data:', err.message);
      setError('Failed to load settings data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettingsChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSystemSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSecuritySettingsChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSecuritySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleTelegramSettingsChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setTelegramSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleProfileSettingsChange = (field) => (event) => {
    setProfileSettings(prev => ({ ...prev, [field]: event.target.value }));
  };

  const saveSystemSettings = async () => {
    setLoading(true);
    setError('');
    try {
      await apiCall(settingsAPI.updateSettings, systemSettings);
      setSuccess('System settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save system settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSecuritySettings = async () => {
    setLoading(true);
    setError('');
    try {
      await apiCall(settingsAPI.updateSettings, securitySettings);
      setSuccess('Security settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save security settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveTelegramSettings = async () => {
    setLoading(true);
    setError('');
    try {
      await apiCall(settingsAPI.updateSettings, telegramSettings);
      setSuccess('Telegram bot settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save telegram settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const testTelegramBot = async () => {
    if (!telegramSettings.botToken) {
      setError('Bot token is required to test the bot');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      // This would be a new API endpoint to test the bot
      const result = await apiCall(settingsAPI.testTelegramBot, {
        botToken: telegramSettings.botToken
      }).catch((err) => {
        throw new Error('Bot test failed: ' + err.message);
      });
      
      if (result.success) {
        setSuccess(`Bot test successful! Bot username: @${result.botInfo.username}`);
        setTelegramSettings(prev => ({ ...prev, botUsername: result.botInfo.username }));
      } else {
        setError('Bot test failed: ' + (result.error || 'Unknown error'));
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveProfileSettings = async () => {
    if (profileSettings.newPassword && profileSettings.newPassword !== profileSettings.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const profileData = {
        fullName: profileSettings.fullName,
        email: profileSettings.email,
      };

      if (profileSettings.newPassword) {
        profileData.currentPassword = profileSettings.currentPassword;
        profileData.newPassword = profileSettings.newPassword;
      }

      await apiCall(settingsAPI.updateProfile, profileData);
      setSuccess('Profile updated successfully!');
      setProfileSettings(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiCall(settingsAPI.createBackup);
      setSuccess('Backup created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create backup: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Logo Management Functions
  const loadCompanyLogo = async () => {
    try {
      const response = await apiCall(logoAPI.getCompanyLogo);
      setLogoSettings(prev => ({
        ...prev,
        companyLogo: response.logo
      }));
    } catch (error) {
      console.error('Failed to load company logo:', error);
    }
  };

  const loadTours = async () => {
    try {
      const response = await apiCall(toursAPI.getTours);
      setTours(response.tours || []);
    } catch (error) {
      console.error('Failed to load tours:', error);
    }
  };

  const handleCompanyLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLogoSettings(prev => ({ ...prev, uploadingLogo: true, logoError: '', logoSuccess: '' }));

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const data = await apiCall(logoAPI.uploadCompanyLogo, formData);

      setLogoSettings(prev => ({ 
        ...prev, 
        companyLogo: data.logo,
        logoSuccess: 'Company logo uploaded successfully!'
      }));

      setTimeout(() => {
        setLogoSettings(prev => ({ ...prev, logoSuccess: '' }));
      }, 3000);

    } catch (error) {
      setLogoSettings(prev => ({ 
        ...prev, 
        logoError: error.message 
      }));
    } finally {
      setLogoSettings(prev => ({ ...prev, uploadingLogo: false }));
    }
  };

  const handleTourLogoUpload = async (tourId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLogoSettings(prev => ({ ...prev, uploadingLogo: true, logoError: '', logoSuccess: '' }));

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const data = await apiCall(logoAPI.uploadTourLogo, tourId, formData);

      setLogoSettings(prev => ({ 
        ...prev, 
        tourLogos: {
          ...prev.tourLogos,
          [tourId]: data.logo
        },
        logoSuccess: `Logo uploaded for ${data.tourName}!`
      }));

      setTimeout(() => {
        setLogoSettings(prev => ({ ...prev, logoSuccess: '' }));
      }, 3000);

    } catch (error) {
      setLogoSettings(prev => ({ 
        ...prev, 
        logoError: error.message 
      }));
    } finally {
      setLogoSettings(prev => ({ ...prev, uploadingLogo: false }));
    }
  };

  const deleteCompanyLogo = async () => {
    setLogoSettings(prev => ({ ...prev, uploadingLogo: true, logoError: '', logoSuccess: '' }));

    try {
      const data = await apiCall(logoAPI.deleteCompanyLogo);

      setLogoSettings(prev => ({ 
        ...prev, 
        companyLogo: null,
        logoSuccess: 'Company logo deleted successfully!'
      }));

      setTimeout(() => {
        setLogoSettings(prev => ({ ...prev, logoSuccess: '' }));
      }, 3000);

    } catch (error) {
      setLogoSettings(prev => ({ 
        ...prev, 
        logoError: error.message 
      }));
    } finally {
      setLogoSettings(prev => ({ ...prev, uploadingLogo: false }));
    }
  };

  const deleteTourLogo = async (tourId) => {
    setLogoSettings(prev => ({ ...prev, uploadingLogo: true, logoError: '', logoSuccess: '' }));

    try {
      const data = await apiCall(logoAPI.deleteTourLogo, tourId);

      setLogoSettings(prev => ({ 
        ...prev, 
        tourLogos: {
          ...prev.tourLogos,
          [tourId]: null
        },
        logoSuccess: `Logo deleted for ${data.tourName}!`
      }));

      setTimeout(() => {
        setLogoSettings(prev => ({ ...prev, logoSuccess: '' }));
      }, 3000);

    } catch (error) {
      setLogoSettings(prev => ({ 
        ...prev, 
        logoError: error.message 
      }));
    } finally {
      setLogoSettings(prev => ({ ...prev, uploadingLogo: false }));
    }
  };

  // QR Design Management Functions
  const loadQRDesigns = async () => {
    setQRDesignSettings(prev => ({ ...prev, loadingTemplates: true }));
    try {
      const response = await fetch('/api/settings/qr-designs', {
        credentials: 'include'
      });
      const data = await response.json();

      setQRDesignSettings(prev => ({
        ...prev,
        systemDefault: data.systemDefault,
        templates: data.templates,
        customDesign: data.systemDefault || prev.customDesign
      }));

      // Generate initial preview
      if (data.systemDefault) {
        generateQRPreview(data.systemDefault);
      }
    } catch (error) {
      console.error('Failed to load QR designs:', error);
      setError('Failed to load QR designs');
    } finally {
      setQRDesignSettings(prev => ({ ...prev, loadingTemplates: false }));
    }
  };

  const generateQRPreview = async (design = null) => {
    const designToUse = design || qrDesignSettings.customDesign;

    setQRDesignSettings(prev => ({ ...prev, generatingPreview: true }));
    try {
      const params = new URLSearchParams({
        foregroundColor: designToUse.foregroundColor,
        backgroundColor: designToUse.backgroundColor,
        dotPattern: designToUse.dotPattern,
        logoEnabled: designToUse.logoEnabled.toString(),
        logoSizePercent: designToUse.logoSizePercent.toString()
      });

      const response = await fetch(`/api/settings/qr-designs/preview?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      setQRDesignSettings(prev => ({
        ...prev,
        previewDataUrl: data.previewDataUrl
      }));
    } catch (error) {
      console.error('Failed to generate QR preview:', error);
    } finally {
      setQRDesignSettings(prev => ({ ...prev, generatingPreview: false }));
    }
  };

  const handleQRDesignChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    setQRDesignSettings(prev => ({
      ...prev,
      customDesign: {
        ...prev.customDesign,
        [field]: value
      },
      selectedTemplate: null // Clear template selection when customizing
    }));

    // Generate preview after a short delay
    setTimeout(() => {
      generateQRPreview({
        ...qrDesignSettings.customDesign,
        [field]: value
      });
    }, 300);
  };

  const applyTemplate = async (template) => {
    setQRDesignSettings(prev => ({
      ...prev,
      selectedTemplate: template.id,
      customDesign: {
        foregroundColor: template.foregroundColor,
        backgroundColor: template.backgroundColor,
        dotPattern: template.dotPattern,
        logoEnabled: template.logoEnabled,
        logoSizePercent: template.logoSizePercent
      }
    }));

    // Apply template to system default
    try {
      const response = await fetch('/api/settings/qr-designs/apply-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ templateId: template.id })
      });

      if (response.ok) {
        setSuccess(`Template "${template.name}" applied successfully!`);
        generateQRPreview(template);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Failed to apply template');
    }
  };

  const saveQRDesign = async () => {
    setQRDesignSettings(prev => ({ ...prev, savingDesign: true }));
    setError('');

    try {
      const response = await fetch('/api/settings/qr-designs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(qrDesignSettings.customDesign)
      });

      if (response.ok) {
        setSuccess('QR design saved successfully!');
        await loadQRDesigns(); // Reload to get updated data
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save QR design');
      }
    } catch (error) {
      setError('Failed to save QR design: ' + error.message);
    } finally {
      setQRDesignSettings(prev => ({ ...prev, savingDesign: false }));
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.fullName || !newUser.password) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const result = await apiCall(settingsAPI.createUser, newUser);
      setAdminUsers(prev => [...prev, result.user]);
      setNewUserDialog(false);
      setNewUser({ username: '', fullName: '', email: '', password: '', role: 'Admin' });
      setSuccess('New admin user created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser({
      id: user.id,
      username: user.username,
      fullName: user.fullName,
      email: user.email,
      active: user.active,
      role: user.role
    });
    setEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser.fullName) {
      setError('Full name is required');
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        fullName: selectedUser.fullName,
        email: selectedUser.email,
        active: selectedUser.active
      };

      const result = await apiCall(settingsAPI.updateUser, selectedUser.id, updateData);
      
      // Update the user in the list
      setAdminUsers(prev => prev.map(u => 
        u.id === selectedUser.id ? { ...u, ...updateData } : u
      ));

      setEditUserDialog(false);
      setSelectedUser(null);
      setSuccess('User updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    // Prevent deleting System Administrator/superadmin or self
    if (user.role === 'System Administrator' || user.role === 'superadmin') {
      setError('Cannot delete System Administrator user');
      return;
    }
    
    if (user.id === currentUser?.id) {
      setError('Cannot delete your own account');  
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete user "${user.fullName}" (${user.username})?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setLoading(true);
    try {
      // Note: We need to create a DELETE endpoint in the backend
      await apiCall(settingsAPI.deleteUser, user.id);
      
      // Remove user from the list
      setAdminUsers(prev => prev.filter(u => u.id !== user.id));

      // Close edit dialog if it's open
      if (editUserDialog && selectedUser?.id === user.id) {
        setEditUserDialog(false);
        setSelectedUser(null);
      }

      setSuccess(`User "${user.fullName}" deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Admin password change function - Updated for superadmin role support
  const handleChangeUserPassword = async (user) => {
    if (currentUser?.role !== 'System Administrator' && currentUser?.role !== 'superadmin') {
      setError('Only System Administrator can change user passwords');
      return;
    }

    const newPassword = prompt(`Enter new password for ${user.fullName}:`);
    if (!newPassword) return;

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await apiCall(settingsAPI.changeUserPassword, user.id, newPassword);
      setSuccess(`Password changed successfully for ${user.fullName}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to change password: ' + err.message);
    } finally {
      setLoading(false);
    }
  };



  return (
    <AdminLayout>
      <Box>
        {/* Modern Header */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: `${theme.palette.primary.main}15`,
                  color: theme.palette.primary.main,
                }}
              >
                <SettingsIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography 
                  variant={isMobile ? "h5" : "h4"} 
                  component="h1" 
                  sx={{ fontWeight: 700, mb: 0.5 }}
                >
                  Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage system configuration, security, and user accounts
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Status Messages */}
          {success && (
            <Fade in={!!success}>
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            </Fade>
          )}
          {error && (
            <Fade in={!!error}>
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            </Fade>
          )}
        </Box>

        {/* Quick Actions */}
        <Box mb={4}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <QuickActionButton
                icon={<BackupIcon />}
                label="Backup"
                description="Create system backup"
                onClick={createBackup}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickActionButton
                icon={<PersonAddIcon />}
                label="Add User"
                description="Create new admin"
                onClick={() => setNewUserDialog(true)}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickActionButton
                icon={<SpeedIcon />}
                label="Performance"
                description="System optimization"
                onClick={() => {}}
                color="warning"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickActionButton
                icon={<ShieldIcon />}
                label="Security"
                description="Security overview"
                onClick={() => {}}
                color="error"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Settings Sections */}
        <Stack spacing={3}>
          {/* System Configuration */}
          <SettingsCard
            icon={<BusinessIcon />}
            title="System Configuration"
            description="Company information, localization, and basic system settings"
            status={{ label: 'Configured', color: 'success' }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Company Information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Company Name"
                    value={systemSettings.companyName}
                    onChange={handleSystemSettingsChange('companyName')}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="QR Code Base URL"
                    value={systemSettings.qrCodeBaseUrl}
                    onChange={handleSystemSettingsChange('qrCodeBaseUrl')}
                    fullWidth
                    size="small"
                    helperText="Base URL for QR code traveler profiles"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Localization
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Default Language"
                    select
                    value={systemSettings.defaultLanguage}
                    onChange={handleSystemSettingsChange('defaultLanguage')}
                    SelectProps={{ native: true }}
                    fullWidth
                    size="small"
                  >
                    <option value="en">English</option>
                    <option value="ar">Arabic</option>
                    <option value="dv">Dhivehi</option>
                  </TextField>
                  
                  <TextField
                    label="Timezone"
                    select
                    value={systemSettings.timezone}
                    onChange={handleSystemSettingsChange('timezone')}
                    SelectProps={{ native: true }}
                    fullWidth
                    size="small"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Maldives">Maldives (UTC+5)</option>
                    <option value="Asia/Riyadh">Saudi Arabia (UTC+3)</option>
                  </TextField>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  System Features
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.emailNotifications}
                          onChange={handleSystemSettingsChange('emailNotifications')}
                          color="primary"
                        />
                      }
                      label="Email Notifications"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.systemBackups}
                          onChange={handleSystemSettingsChange('systemBackups')}
                          color="primary"
                        />
                      }
                      label="Automatic Backups"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.auditLogging}
                          onChange={handleSystemSettingsChange('auditLogging')}
                          color="primary"
                        />
                      }
                      label="Audit Logging"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" pt={2}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={saveSystemSettings}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    Save Configuration
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SettingsCard>

          {/* Logo Management */}
          <SettingsCard
            icon={<PhotoIcon />}
            title="Logo Management"
            description="Upload and manage company and tour-specific logos for traveler profiles"
            status={{ label: logoSettings.companyLogo ? 'Configured' : 'Not Configured', color: logoSettings.companyLogo ? 'success' : 'warning' }}
          >
            {logoSettings.logoSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {logoSettings.logoSuccess}
              </Alert>
            )}
            
            {logoSettings.logoError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {logoSettings.logoError}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Company Logo */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Company Logo
                </Typography>
                <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      {logoSettings.companyLogo ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'grey.50'
                          }}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_URL || ''}/api/logos/view/company/${logoSettings.companyLogo.path.split('/').pop()}`}
                            alt="Company Logo"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 2,
                            borderStyle: 'dashed',
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            bgcolor: 'grey.50'
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No company logo uploaded
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                          Upload your company logo to be displayed on all traveler profiles. 
                          Recommended size: 400x200px. Max file size: 5MB.
                        </Typography>
                        
                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={logoSettings.uploadingLogo ? <CircularProgress size={16} /> : <UploadIcon />}
                            disabled={logoSettings.uploadingLogo}
                            sx={{ borderRadius: 2 }}
                          >
                            {logoSettings.companyLogo ? 'Replace Logo' : 'Upload Logo'}
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              hidden
                              onChange={handleCompanyLogoUpload}
                            />
                          </Button>
                          
                          {logoSettings.companyLogo && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={logoSettings.uploadingLogo ? <CircularProgress size={16} /> : <DeleteIcon />}
                              disabled={logoSettings.uploadingLogo}
                              onClick={deleteCompanyLogo}
                              sx={{ borderRadius: 2 }}
                            >
                              Delete Logo
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Tour Logos */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Tour-Specific Logos
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload custom logos for individual tours. These will be displayed alongside the company logo on traveler profiles.
                </Typography>
                
                {tours.length === 0 ? (
                  <Alert severity="info">
                    No tours available. Create tours first to upload tour-specific logos.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {tours.map((tour) => (
                      <Paper key={tour.id} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            {logoSettings.tourLogos?.[tour.id] ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 80,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  bgcolor: 'grey.50'
                                }}
                              >
                                <img
                                  src={`${process.env.REACT_APP_API_URL || ''}/api/logos/view/tour/${logoSettings.tourLogos[tour.id].path.split('/').pop()}`}
                                  alt={`${tour.name} Logo`}
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 80,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: 1,
                                  borderStyle: 'dashed',
                                  borderColor: 'grey.300',
                                  borderRadius: 2,
                                  bgcolor: 'grey.50'
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No logo
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                          
                          <Grid item xs={12} sm={9}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {tour.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {tour.destination} • {tour.total_travelers || 0} travelers
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    component="label"
                                    startIcon={logoSettings.uploadingLogo ? <CircularProgress size={14} /> : <UploadIcon />}
                                    disabled={logoSettings.uploadingLogo}
                                  >
                                    {logoSettings.tourLogos?.[tour.id] ? 'Replace' : 'Upload'}
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/jpg,image/png,image/webp"
                                      hidden
                                      onChange={(e) => handleTourLogoUpload(tour.id, e)}
                                    />
                                  </Button>
                                  
                                  {logoSettings.tourLogos?.[tour.id] && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={logoSettings.uploadingLogo ? <CircularProgress size={14} /> : <DeleteIcon />}
                                      disabled={logoSettings.uploadingLogo}
                                      onClick={() => deleteTourLogo(tour.id)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </Stack>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </SettingsCard>

          {/* QR Code Design Customization */}
          <SettingsCard
            icon={<QrCodeIcon />}
            title="QR Code Design Customization"
            description="Customize QR code appearance with colors, patterns, and logo embedding"
            status={{ label: qrDesignSettings.systemDefault ? 'Configured' : 'Default', color: qrDesignSettings.systemDefault ? 'success' : 'info' }}
          >
            {qrDesignSettings.loadingTemplates ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Template Gallery */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Pre-Designed Templates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select a template to quickly apply a professional design to your QR codes
                  </Typography>

                  <Grid container spacing={2}>
                    {qrDesignSettings.templates && qrDesignSettings.templates.map((template) => (
                      <Grid item xs={6} sm={4} md={3} key={template.id}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            border: 2,
                            borderColor: qrDesignSettings.selectedTemplate === template.id ? 'primary.main' : 'divider',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              transform: 'translateY(-4px)',
                              boxShadow: 4
                            }
                          }}
                          onClick={() => applyTemplate(template)}
                        >
                          <Box
                            sx={{
                              p: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              bgcolor: template.backgroundColor,
                              minHeight: 140
                            }}
                          >
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                bgcolor: template.foregroundColor,
                                borderRadius: template.dotPattern === 'circular' ? '50%' : template.dotPattern === 'rounded' ? 2 : 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1
                              }}
                            >
                              <QrCodeIcon sx={{ color: template.backgroundColor, fontSize: 48 }} />
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center' }}>
                              {template.name}
                            </Typography>
                            {qrDesignSettings.selectedTemplate === template.id && (
                              <CheckIcon color="primary" sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                {/* Manual Customization */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Manual Customization
                  </Typography>

                  <Stack spacing={3}>
                    {/* Color Pickers */}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Colors
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            label="Foreground Color"
                            type="color"
                            value={qrDesignSettings.customDesign.foregroundColor}
                            onChange={handleQRDesignChange('foregroundColor')}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Background Color"
                            type="color"
                            value={qrDesignSettings.customDesign.backgroundColor}
                            onChange={handleQRDesignChange('backgroundColor')}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Dot Pattern Selector */}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Dot Pattern
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          { value: 'square', label: 'Square', icon: '⬛' },
                          { value: 'rounded', label: 'Rounded', icon: '⬜' },
                          { value: 'circular', label: 'Circular', icon: '⚫' }
                        ].map((pattern) => (
                          <Grid item xs={4} key={pattern.value}>
                            <Card
                              sx={{
                                cursor: 'pointer',
                                border: 2,
                                borderColor: qrDesignSettings.customDesign.dotPattern === pattern.value ? 'primary.main' : 'divider',
                                textAlign: 'center',
                                py: 2,
                                '&:hover': {
                                  borderColor: 'primary.main'
                                }
                              }}
                              onClick={() => {
                                handleQRDesignChange('dotPattern')({ target: { value: pattern.value } });
                              }}
                            >
                              <Typography variant="h4">{pattern.icon}</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {pattern.label}
                              </Typography>
                              {qrDesignSettings.customDesign.dotPattern === pattern.value && (
                                <CheckIcon color="primary" sx={{ mt: 0.5, fontSize: 16 }} />
                              )}
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Logo Settings */}
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={qrDesignSettings.customDesign.logoEnabled}
                            onChange={handleQRDesignChange('logoEnabled')}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Embed Logo
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Center company logo in QR code
                            </Typography>
                          </Box>
                        }
                      />

                      {qrDesignSettings.customDesign.logoEnabled && (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            label="Logo Size (%)"
                            type="number"
                            value={qrDesignSettings.customDesign.logoSizePercent}
                            onChange={handleQRDesignChange('logoSizePercent')}
                            fullWidth
                            size="small"
                            inputProps={{ min: 10, max: 30, step: 1 }}
                            helperText="Logo size as percentage of QR code (10-30%)"
                          />
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Grid>

                {/* Live Preview */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Live Preview
                  </Typography>

                  <Paper
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      minHeight: 300
                    }}
                  >
                    {qrDesignSettings.generatingPreview ? (
                      <CircularProgress />
                    ) : qrDesignSettings.previewDataUrl ? (
                      <>
                        <Box
                          component="img"
                          src={qrDesignSettings.previewDataUrl}
                          alt="QR Code Preview"
                          sx={{
                            width: 250,
                            height: 250,
                            objectFit: 'contain',
                            mb: 2
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                          This is how your QR codes will appear
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Preview will appear here
                      </Typography>
                    )}
                  </Paper>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Changes will apply to newly generated QR codes.
                      Existing QR codes can be regenerated to apply the new design.
                    </Typography>
                  </Alert>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setQRDesignSettings(prev => ({
                          ...prev,
                          customDesign: {
                            foregroundColor: '#000000',
                            backgroundColor: '#FFFFFF',
                            dotPattern: 'square',
                            logoEnabled: false,
                            logoSizePercent: 20
                          },
                          selectedTemplate: null
                        }));
                        generateQRPreview({
                          foregroundColor: '#000000',
                          backgroundColor: '#FFFFFF',
                          dotPattern: 'square',
                          logoEnabled: false,
                          logoSizePercent: 20
                        });
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      Reset to Default
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={qrDesignSettings.savingDesign ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={saveQRDesign}
                      disabled={qrDesignSettings.savingDesign}
                      sx={{ borderRadius: 2 }}
                    >
                      Save QR Design
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </SettingsCard>

          {/* Security Settings */}
          <SettingsCard
            icon={<SecurityIcon />}
            title="Security & Authentication"
            description="Password policies, session management, and access controls"
            status={{ label: 'Secure', color: 'success' }}
            badge={2}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Authentication
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Session Timeout (minutes)"
                    type="number"
                    value={securitySettings.sessionTimeout}
                    onChange={handleSecuritySettingsChange('sessionTimeout')}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Maximum Login Attempts"
                    type="number"
                    value={securitySettings.maxLoginAttempts}
                    onChange={handleSecuritySettingsChange('maxLoginAttempts')}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="API Rate Limit (req/min)"
                    type="number"
                    value={securitySettings.apiRateLimit}
                    onChange={handleSecuritySettingsChange('apiRateLimit')}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Password Policies
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Minimum Password Length"
                    type="number"
                    value={securitySettings.passwordMinLength}
                    onChange={handleSecuritySettingsChange('passwordMinLength')}
                    fullWidth
                    size="small"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={securitySettings.requireSpecialChars}
                        onChange={handleSecuritySettingsChange('requireSpecialChars')}
                      />
                    }
                    label="Require Special Characters"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={securitySettings.twoFactorAuth}
                        onChange={handleSecuritySettingsChange('twoFactorAuth')}
                      />
                    }
                    label="Two-Factor Authentication"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" pt={2}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={saveSecuritySettings}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    Save Security Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SettingsCard>

          {/* Telegram Bot Configuration */}
          <SettingsCard
            icon={<TelegramIcon />}
            title="Telegram Bot Configuration"
            description="Configure the Telegram bot for document management and staff communication"
            status={telegramSettings.botEnabled ? { label: 'Enabled', color: 'success' } : { label: 'Disabled', color: 'error' }}
            badge={telegramSettings.botToken ? null : 1}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  To set up the Telegram bot:
                  <br />1. Create a new bot with @BotFather on Telegram
                  <br />2. Get your bot token and paste it below
                  <br />3. Test the bot connection
                  <br />4. Enable the bot to start receiving documents
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Bot Configuration
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Bot Token"
                    type="password"
                    value={telegramSettings.botToken}
                    onChange={handleTelegramSettingsChange('botToken')}
                    fullWidth
                    size="small"
                    helperText="Get this from @BotFather on Telegram"
                    placeholder="1234567890:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                  />
                  <TextField
                    label="Bot Username"
                    value={telegramSettings.botUsername}
                    onChange={handleTelegramSettingsChange('botUsername')}
                    fullWidth
                    size="small"
                    helperText="Will be filled automatically after testing"
                    placeholder="@your_bot_username"
                  />
                  <TextField
                    label="Webhook URL"
                    value={telegramSettings.webhookUrl}
                    onChange={handleTelegramSettingsChange('webhookUrl')}
                    fullWidth
                    size="small"
                    helperText="Leave empty to use default webhook endpoint"
                    placeholder="https://your-domain.com/webhook/telegram"
                  />
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={testTelegramBot}
                      disabled={loading || !telegramSettings.botToken}
                      size="small"
                      sx={{ borderRadius: 2 }}
                    >
                      Test Bot Connection
                    </Button>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Document Settings
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Allowed File Types"
                    value={telegramSettings.allowedFileTypes}
                    onChange={handleTelegramSettingsChange('allowedFileTypes')}
                    fullWidth
                    size="small"
                    helperText="Comma-separated file extensions"
                  />
                  <TextField
                    label="Max File Size (MB)"
                    type="number"
                    value={telegramSettings.maxFileSize}
                    onChange={handleTelegramSettingsChange('maxFileSize')}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Welcome Message"
                    multiline
                    rows={2}
                    value={telegramSettings.botWelcomeMessage}
                    onChange={handleTelegramSettingsChange('botWelcomeMessage')}
                    fullWidth
                    size="small"
                    helperText="Message sent when users start the bot"
                  />
                  <TextField
                    label="Help Message"
                    multiline
                    rows={2}
                    value={telegramSettings.botHelpMessage}
                    onChange={handleTelegramSettingsChange('botHelpMessage')}
                    fullWidth
                    size="small"
                    helperText="Message sent when users ask for help"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Bot Features
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={telegramSettings.botEnabled}
                          onChange={handleTelegramSettingsChange('botEnabled')}
                          color="primary"
                        />
                      }
                      label="Enable Bot"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={telegramSettings.documentUploadEnabled}
                          onChange={handleTelegramSettingsChange('documentUploadEnabled')}
                          color="primary"
                        />
                      }
                      label="Document Upload"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={telegramSettings.staffApprovalRequired}
                          onChange={handleTelegramSettingsChange('staffApprovalRequired')}
                          color="primary"
                        />
                      }
                      label="Staff Approval Required"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={telegramSettings.autoProcessDocuments}
                          onChange={handleTelegramSettingsChange('autoProcessDocuments')}
                          color="primary"
                        />
                      }
                      label="Auto-Process Documents"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => setTelegramSettings({
                      botToken: '',
                      webhookUrl: '',
                      botUsername: '',
                      botEnabled: false,
                      documentUploadEnabled: true,
                      staffApprovalRequired: true,
                      allowedFileTypes: 'jpg,jpeg,png,pdf',
                      maxFileSize: '10',
                      autoProcessDocuments: true,
                      botWelcomeMessage: 'Welcome! Please upload your travel documents.',
                      botHelpMessage: 'Send me your documents and I\'ll help you organize them.',
                    })}
                    sx={{ borderRadius: 2 }}
                  >
                    Reset to Defaults
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={saveTelegramSettings}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    Save Bot Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SettingsCard>

          {/* User Management */}
          <SettingsCard
            icon={<GroupIcon />}
            title="User Management"
            description="Admin users, roles, and access permissions"
            badge={adminUsers.length}
          >
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Admin Users ({adminUsers.length})
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setNewUserDialog(true)}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Add User
                </Button>
              </Box>

              <Stack spacing={2}>
                {adminUsers.map((user) => (
                  <Card key={user.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar 
                            sx={{ 
                              bgcolor: user.active ? 'success.main' : 'grey.500',
                              width: 40,
                              height: 40,
                            }}
                          >
                            {user.fullName?.charAt(0) || user.username?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {user.fullName || user.username}
                              </Typography>
                              <Chip
                                label={user.role || 'Admin'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {!user.active && <Chip label="Inactive" size="small" color="error" />}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              @{user.username} • {user.email || 'No email'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Edit User">
                            <IconButton size="small" onClick={() => handleEditUser(user)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                
                {adminUsers.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No admin users found
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </SettingsCard>

          {/* Profile Settings */}
          <SettingsCard
            icon={<PersonIcon />}
            title="My Profile"
            description="Update your personal information and password"
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Profile Information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Full Name"
                    value={profileSettings.fullName}
                    onChange={handleProfileSettingsChange('fullName')}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Username"
                    value={profileSettings.username}
                    onChange={handleProfileSettingsChange('username')}
                    fullWidth
                    disabled
                    size="small"
                    helperText="Username cannot be changed"
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={profileSettings.email}
                    onChange={handleProfileSettingsChange('email')}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Change Password
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Current Password"
                    type="password"
                    value={profileSettings.currentPassword}
                    onChange={handleProfileSettingsChange('currentPassword')}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={profileSettings.newPassword}
                    onChange={handleProfileSettingsChange('newPassword')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value={profileSettings.confirmPassword}
                    onChange={handleProfileSettingsChange('confirmPassword')}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" pt={2}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={saveProfileSettings}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    Update Profile
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SettingsCard>

          {/* System Status */}
          <SettingsCard
            icon={<StorageIcon />}
            title="System Status"
            description="Monitor system health, storage, and performance"
            status={{ label: 'Healthy', color: 'success' }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  System Health
                </Typography>
                <Stack spacing={1}>
                  <SystemStatus 
                    label="Database Connection" 
                    status={systemStatus.database} 
                    description="PostgreSQL connection active"
                  />
                  <SystemStatus 
                    label="API Services" 
                    status={systemStatus.api} 
                    description="All endpoints responding"
                  />
                  <SystemStatus 
                    label="File Storage" 
                    status={systemStatus.storage} 
                    description="Document uploads working"
                  />
                  <SystemStatus 
                    label="Backup System" 
                    status={systemStatus.backup} 
                    description="Last backup: Never"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  System Information
                </Typography>
                <Stack spacing={2}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Version
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Haji Profiles Alpha v1.0.0
                    </Typography>
                  </Card>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Storage Used
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ~50MB
                    </Typography>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </SettingsCard>
        </Stack>

        {/* New User Dialog */}
        <Dialog open={newUserDialog} onClose={() => setNewUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Create New Admin User
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Username"
                value={newUser.username}
                onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Full Name"
                value={newUser.fullName}
                onChange={(e) => setNewUser(prev => ({ ...prev, fullName: e.target.value }))}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Role"
                select
                value={newUser.role}
                onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                SelectProps={{ native: true }}
                fullWidth
                size="small"
              >
                <option value="Administrator">Administrator</option>
                <option value="System Administrator">System Administrator</option>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setNewUserDialog(false)} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              variant="contained"
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Edit Admin User
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Stack spacing={2} mt={1}>
                <TextField
                  label="Username"
                  value={selectedUser.username}
                  disabled
                  fullWidth
                  size="small"
                  helperText="Username cannot be changed"
                />
                <TextField
                  label="Full Name"
                  value={selectedUser.fullName}
                  onChange={(e) => setSelectedUser(prev => ({ ...prev, fullName: e.target.value }))}
                  fullWidth
                  required
                  size="small"
                />
                <TextField
                  label="Email"
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser(prev => ({ ...prev, email: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedUser.active}
                      onChange={(e) => setSelectedUser(prev => ({ ...prev, active: e.target.checked }))}
                    />
                  }
                  label={selectedUser.active ? "Active User" : "Inactive User"}
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Box sx={{ display: 'flex', flex: 1, gap: 1 }}>
              {(currentUser?.role === 'System Administrator' || currentUser?.role === 'superadmin') && selectedUser?.role !== 'System Administrator' && selectedUser?.role !== 'superadmin' && (
                <Button
                  onClick={() => handleDeleteUser(selectedUser)}
                  color="error"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Delete User
                </Button>
              )}
              {(currentUser?.role === 'System Administrator' || currentUser?.role === 'superadmin') && (
                <Button
                  onClick={() => handleChangeUserPassword(selectedUser)}
                  color="warning"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Reset Password
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setEditUserDialog(false)} sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateUser}
                variant="contained"
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Update User
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default Settings;